import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { authFetch } from "@/lib/authFetch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import {
  ShoppingCart, RefreshCw, AlertTriangle, ExternalLink, Search,
  CalendarDays, TrendingUp, Clock, Users, Package,
} from "lucide-react";
import type { Commande } from "@shared/schema";
import { useProductImageResolver } from "@/hooks/use-inventory";

const STATUTS_LABELS: Record<string, { label: string; color: string }> = {
  draft: { label: "Brouillon", color: "bg-gray-100 text-gray-700" },
  confirmed: { label: "Confirmée", color: "bg-blue-100 text-blue-700" },
  invoiced: { label: "Facturée", color: "bg-indigo-100 text-indigo-700" },
  shipped: { label: "Expédiée", color: "bg-orange-100 text-orange-700" },
  partially_shipped: { label: "Part. expédiée", color: "bg-yellow-100 text-yellow-700" },
  delivered: { label: "Livrée", color: "bg-green-100 text-green-700" },
  fulfilled: { label: "Livrée", color: "bg-green-100 text-green-700" },
  cancelled: { label: "Annulée", color: "bg-red-100 text-red-700" },
  void: { label: "Annulée", color: "bg-red-100 text-red-700" },
};

function statutBadge(statut: string | null | undefined) {
  const s = statut?.toLowerCase() || "";
  const info = STATUTS_LABELS[s] || { label: statut || "—", color: "bg-gray-100 text-gray-600" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${info.color}`}>
      {info.label}
    </span>
  );
}

function typeBadge(type: string) {
  return type === "revendeur"
    ? <Badge className="bg-violet-100 text-violet-700 border-0">Revendeur</Badge>
    : <Badge className="bg-blue-100 text-blue-700 border-0">Client</Badge>;
}

function formatDate(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function formatMontant(m: string | null | undefined) {
  if (!m) return "—";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(parseFloat(m));
}

function ProductThumbnails({ lignes, resolveImage }: { lignes: any[]; resolveImage: (sku?: string | null, fallback?: string | null) => string | null }) {
  const resolved = (lignes || []).map(l => ({ ...l, resolvedImg: resolveImage(l.sku, l.imageUrl) }));
  const withImages = resolved.filter(l => l.resolvedImg);
  const rest = withImages.length > 3 ? withImages.length - 3 : 0;
  return (
    <div className="flex items-center gap-1">
      {withImages.slice(0, 3).map((l, i) => (
        <img key={i} src={l.resolvedImg!} alt={l.nom} className="w-8 h-8 rounded object-cover border border-border" />
      ))}
      {rest > 0 && (
        <span className="text-xs text-muted-foreground bg-muted rounded-full px-1.5 py-0.5">+{rest}</span>
      )}
      {withImages.length === 0 && (
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Package className="w-3 h-3" />{(lignes || []).length} produit{(lignes || []).length > 1 ? "s" : ""}
        </span>
      )}
    </div>
  );
}

export default function CommandesList() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const resolveImage = useProductImageResolver();

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statutFilter, setStatutFilter] = useState("all");
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncPhaseLabel, setSyncPhaseLabel] = useState("");

  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (typeFilter !== "all") params.set("type", typeFilter);
  if (statutFilter !== "all") params.set("statut", statutFilter);
  if (dateDebut) params.set("dateDebut", dateDebut);
  if (dateFin) params.set("dateFin", dateFin);

  const { data: commandes = [], isLoading } = useQuery<Commande[]>({
    queryKey: ["/api/commandes", search, typeFilter, statutFilter, dateDebut, dateFin],
    queryFn: () => authFetch(`/api/commandes?${params.toString()}`).then(r => r.json()),
  });

  const { data: stats } = useQuery<{ total: number; totalMois: number; caMoisHt: number; enAttente: number; clientsMois: number }>({
    queryKey: ["/api/commandes/stats"],
    queryFn: () => authFetch("/api/commandes/stats").then(r => r.json()),
  });

  const { data: zohoStatus } = useQuery<{ connected: boolean; updatedAt?: string }>({
    queryKey: ["/api/zoho/status"],
    queryFn: () => authFetch("/api/zoho/status").then(r => r.json()),
  });

  const PHASE_LABELS: Record<string, string> = {
    sales_orders: "Commandes client…",
    billing: "Factures Zoho Billing…",
    purchase_orders: "Commandes fournisseur…",
    done: "Terminé",
    error: "Erreur",
  };

  async function handleSync() {
    setSyncing(true);
    setSyncProgress(5);
    setSyncPhaseLabel("Démarrage…");
    try {
      const startRes = await authFetch("/api/zoho/sync", { method: "POST" });
      const startData = await startRes.json();
      if (!startRes.ok) throw new Error(startData.message || "Erreur de démarrage");

      // Polling du statut toutes les 2 secondes
      await new Promise<void>((resolve, reject) => {
        const poll = setInterval(async () => {
          try {
            const statusRes = await authFetch("/api/zoho/sync/status");
            const state = await statusRes.json();
            const phase: string = state.phase || "idle";
            setSyncPhaseLabel(PHASE_LABELS[phase] || phase);

            // Progression approximative selon la phase
            if (phase === "sales_orders") setSyncProgress(20);
            else if (phase === "billing") setSyncProgress(55);
            else if (phase === "purchase_orders") setSyncProgress(80);
            else if (phase === "done") {
              setSyncProgress(100);
              clearInterval(poll);
              toast({
                title: "Synchronisation terminée",
                description: `${state.imported} cmd importées, ${state.updated} mises à jour · ${state.facturesImported} factures · ${state.poImported} achats${state.errors || state.factureErrors || state.poErrors ? ` · ${(state.errors||0)+(state.factureErrors||0)+(state.poErrors||0)} erreurs` : ""}`,
              });
              queryClient.invalidateQueries({ queryKey: ["/api/commandes"] });
              queryClient.invalidateQueries({ queryKey: ["/api/commandes/stats"] });
              queryClient.invalidateQueries({ queryKey: ["/api/zoho/status"] });
              resolve();
            } else if (phase === "error") {
              clearInterval(poll);
              reject(new Error(state.lastError || "Erreur inconnue"));
            }
          } catch (pollErr: any) {
            clearInterval(poll);
            reject(pollErr);
          }
        }, 2000);
      });
    } catch (e: any) {
      toast({ title: "Erreur synchronisation", description: e.message, variant: "destructive" });
    } finally {
      setTimeout(() => { setSyncing(false); setSyncProgress(0); setSyncPhaseLabel(""); }, 1500);
    }
  }

  const derniereSync = zohoStatus?.updatedAt
    ? new Date(zohoStatus.updatedAt).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">Commandes</h1>
          <p className="text-muted-foreground mt-1">
            {commandes.length} commande{commandes.length > 1 ? "s" : ""}
            {derniereSync && <span className="ml-2 text-xs">· Dernière sync : {derniereSync}</span>}
          </p>
        </div>
        <Button
          onClick={handleSync}
          disabled={syncing || !zohoStatus?.connected}
          data-testid="button-sync-zoho"
          className="gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Synchronisation…" : "Synchroniser avec Zoho"}
        </Button>
      </div>

      {/* Progress bar when syncing */}
      {syncing && (
        <div className="space-y-1">
          <Progress value={syncProgress} className="h-1.5" />
          {syncPhaseLabel && (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse inline-block" />
              {syncPhaseLabel}
            </p>
          )}
        </div>
      )}

      {/* Zoho not connected alert */}
      {zohoStatus && !zohoStatus.connected && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="flex items-center justify-between">
            <span className="text-amber-800">Zoho Inventory n'est pas encore connecté. Connectez votre compte pour synchroniser les commandes.</span>
            <Button
              variant="outline"
              size="sm"
              className="ml-4 border-amber-300 text-amber-800 hover:bg-amber-100 shrink-0"
              onClick={() => window.location.href = "/api/zoho/auth"}
              data-testid="button-connect-zoho"
            >
              <ExternalLink className="w-3 h-3 mr-1" />
              Connecter Zoho Inventory
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50"><ShoppingCart className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Commandes ce mois</p>
              <p className="text-2xl font-bold">{stats?.totalMois ?? "—"}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-50"><TrendingUp className="w-5 h-5 text-green-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">CA du mois HT</p>
              <p className="text-2xl font-bold">
                {stats ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(stats.caMoisHt) : "—"}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-50"><Clock className="w-5 h-5 text-orange-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">En attente livraison</p>
              <p className="text-2xl font-bold">{stats?.enAttente ?? "—"}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-violet-50"><Users className="w-5 h-5 text-violet-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Clients actifs ce mois</p>
              <p className="text-2xl font-bold">{stats?.clientsMois ?? "—"}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par numéro ou client…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            data-testid="input-search-commandes"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-36" data-testid="select-type-commande">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            <SelectItem value="client">Client</SelectItem>
            <SelectItem value="revendeur">Revendeur</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statutFilter} onValueChange={setStatutFilter}>
          <SelectTrigger className="w-40" data-testid="select-statut-commande">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="draft">Brouillon</SelectItem>
            <SelectItem value="confirmed">Confirmée</SelectItem>
            <SelectItem value="invoiced">Facturée</SelectItem>
            <SelectItem value="shipped">Expédiée</SelectItem>
            <SelectItem value="partially_shipped">Part. expédiée</SelectItem>
            <SelectItem value="delivered">Livrée</SelectItem>
            <SelectItem value="fulfilled">Livrée (Zoho)</SelectItem>
            <SelectItem value="cancelled">Annulée</SelectItem>
            <SelectItem value="void">Annulée (void)</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-muted-foreground" />
          <Input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} className="w-36" data-testid="input-date-debut" />
          <span className="text-muted-foreground text-sm">→</span>
          <Input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} className="w-36" data-testid="input-date-fin" />
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-xl overflow-hidden bg-card shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Numéro</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Destinataire</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Type</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Produits</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Montant HT</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Statut</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Date</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-t">
                    {Array.from({ length: 8 }).map((_, j) => (
                      <td key={j} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td>
                    ))}
                  </tr>
                ))
              ) : commandes.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-16 text-muted-foreground">
                    <ShoppingCart className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p className="font-medium">Aucune commande</p>
                    <p className="text-xs mt-1">Synchronisez avec Zoho pour importer les commandes</p>
                  </td>
                </tr>
              ) : (
                commandes.map(c => (
                  <tr
                    key={c.id}
                    className="border-t hover:bg-muted/30 cursor-pointer transition-colors"
                    onClick={() => navigate(`/commandes/${c.id}`)}
                    data-testid={`row-commande-${c.id}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs font-medium">{c.zohoNumero || c.id.slice(0, 8)}</td>
                    <td className="px-4 py-3 font-medium">{c.contactNom || "—"}</td>
                    <td className="px-4 py-3">{typeBadge(c.type)}</td>
                    <td className="px-4 py-3"><ProductThumbnails lignes={(c.lignes as any) || []} resolveImage={resolveImage} /></td>
                    <td className="px-4 py-3 text-right font-medium">{formatMontant(c.montantHt)}</td>
                    <td className="px-4 py-3">{statutBadge(c.statut)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{formatDate(c.dateCommande)}</td>
                    <td className="px-4 py-3">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={e => {
                          e.stopPropagation();
                          navigate(`/commandes/${c.id}`);
                        }}
                        data-testid={`button-detail-commande-${c.id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
